import React, {useState, useEffect} from 'react';
import {jwtDecode} from 'jwt-decode';
import WelcomeHeader from './WelcomeHeader';
import ReactECharts from 'echarts-for-react';
import {Container, Row, Col} from 'react-bootstrap';
import {getHardwareUsage} from './hardwareApi';
import {getAlertSummary} from './alertApi';
import './DashboardPage.css';

interface MyJwtPayload {
    username?: string;
    user_type?: string;
    exp?: number;
}

interface HardwareUsage {
    cpuUsage: number;
    memoryUsedGB: number;
    memoryTotalGB: number;
    diskUsedGB: number;
    diskTotalGB: number;
}

interface AlertSummary {
    organizations: string[];
    totalMonitoringItems: number[];
    firingMonitoringItems: number[];
    firingAlertNames: Record<string, string[]>; // **添加这个字段**
}


const DashboardPage: React.FC = () => {
    const [userType, setUserType] = useState<string | null>(null);
    const [hardwareData, setHardwareData] = useState<HardwareUsage | null>(null);
    const [alertData, setAlertData] = useState<AlertSummary | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            const decoded = jwtDecode<MyJwtPayload>(token);
            if (decoded.exp && decoded.exp * 1000 > Date.now()) {
                setUserType(decoded.user_type || 'Standard User');
            } else {
                console.warn('Token expired');
            }
        }
    }, []);

    useEffect(() => {
        if (!userType) return;

        let intervalId: NodeJS.Timeout | null = null; // 定时器 ID

        const fetchData = async () => {
            setLoading(true);

            if (userType === 'platform_admin') {
                const hardware = await getHardwareUsage();
                console.log('✅ Hardware Usage Data:', hardware);
                setHardwareData(hardware);
            } else {
                const alertSummary = await getAlertSummary();
                console.log('✅ Alert Summary Data:', alertSummary);
                setAlertData(alertSummary);
            }

            setLoading(false);
        };

        fetchData(); // 组件挂载时立即执行一次

        intervalId = setInterval(() => {
            console.log("⏳ Fetching data...");
            fetchData();
        }, 60000); // 每 60 秒刷新一次

        return () => {
            if (intervalId) {
                clearInterval(intervalId); // 组件卸载时清除定时器
                console.log("🛑 Stopped auto-refresh.");
            }
        };
    }, [userType]); // 依赖 `userType` 变化时重新启动定时器


    const getHardwareUsageOption = () => {
        if (!hardwareData) {
            // 如果没有硬件数据，则返回一个空的配置
            return {
                title: {
                    text: 'No hardware data available',
                    left: 'center',
                    textStyle: {color: '#FFFFFF'}
                },
                xAxis: {
                    type: 'value'
                },
                yAxis: {
                    type: 'category',
                    data: ['CPU (%)', 'Memory (GB)', 'Disk (GB)']
                },
                series: []
            };
        }


        return {
            title: {
                text: 'Server Hardware Resource Usage for Lynxguard',
                left: 'center',
                top: 10,
                textStyle: {color: '#FFFFFF', fontWeight: 'bold'}
            },
            tooltip: {trigger: 'axis', axisPointer: {type: 'shadow'}},
            legend: {
                data: ['Used Capacity', 'Free Capacity'],
                top: 40,
                left: 'center',
                textStyle: {color: '#FFFFFF'}
            },
            grid: {
                left: '5%',
                right: '5%',
                bottom: '5%',
                top: 80,  // **减少顶部留白**
                containLabel: true
            },
            xAxis: {
                type: 'value',
                boundaryGap: [0, 0.01],
                axisLabel: {show: false},
                splitLine: {show: false},
                axisTick: {show: false},
                axisLine: {show: false}
            },
            yAxis: {
                type: 'category',
                data: ['CPU (%)', 'Memory (GB)', 'Disk (GB)'],
                axisLine: {lineStyle: {color: '#FFFFFF'}},
                axisTick: {show: false},
                axisLabel: {margin: 20} // **增加类别间距**
            },
            series: [
                {
                    name: 'Used Capacity',
                    type: 'bar',
                    stack: 'total',
                    barWidth: 50,   // **让条形变高**
                    barMaxWidth: 80, // **允许更大高度**
                    label: {
                        show: true,
                        position: 'inside',
                        formatter: (params: { dataIndex: number; value: any; }) => {
                            if (params.dataIndex === 0) {
                                return `${params.value}%\nused`;
                            }
                            return `${params.value} GB\nused`;
                        }
                    },
                    data: [
                        Math.round(hardwareData.cpuUsage),
                        Math.round(hardwareData.memoryUsedGB),
                        Math.round(hardwareData.diskUsedGB)
                    ],
                    itemStyle: {color: '#ff6b6b'}
                },
                {
                    name: 'Free Capacity',
                    type: 'bar',
                    stack: 'total',
                    barWidth: 50,
                    barMaxWidth: 80,
                    label: {
                        show: true,
                        position: 'inside',
                        distance: 10,
                        formatter: (params: { dataIndex: number; value: any; }) => {
                            if (params.dataIndex === 0) {
                                return `${params.value}%\nfree`;
                            }
                            return `${params.value}GB\nfree`;
                        }
                    },
                    data: [
                        Math.max(0, Math.round(100 - hardwareData.cpuUsage)),
                        Math.max(0, Math.round(hardwareData.memoryTotalGB - hardwareData.memoryUsedGB)),
                        Math.max(0, Math.round(hardwareData.diskTotalGB - hardwareData.diskUsedGB))
                    ],
                    itemStyle: {color: '#4caf50'}
                }
            ]
        };
    };


    const getAlertSummaryOption = () => {
        if (!alertData || !alertData.organizations || !alertData.totalMonitoringItems || !alertData.firingMonitoringItems) {
            // 如果没有警报数据或必要的字段为空，则返回一个空的配置
            return {
                title: {
                    text: 'No alert data available',
                    left: 'center',
                    textStyle: {color: '#FFFFFF'}
                },
                xAxis: {
                    type: 'value'
                },
                yAxis: {
                    type: 'category',
                    data: []
                },
                series: []
            };
        }

        // 计算正常和异常监控项的数量，同时处理可能的 null 值
        const totalItemsAdjusted = alertData.totalMonitoringItems.map((total, index) => {
            const firing = alertData.firingMonitoringItems[index] || 0;
            return total ? total - firing : 0;
        });

        const firingItemsAdjusted = alertData.firingMonitoringItems.map(item => item || 0);

        return {
            title: {
                text: 'Alert Summary',
                left: 'center',
                top: 10,                 // ✅ 用 px 更稳
                textStyle: {color: '#FFFFFF', fontWeight: 'bold'}
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {type: 'shadow'},
                formatter: (params: any) => {
                    // 检查是否有 Anomaly monitoring items
                    const hasAnomaly = params.some((item: any) => item.seriesName === 'Anomaly monitoring items' && item.value > 0);

                    if (!hasAnomaly) {
                        return '';
                    }

                    let tooltipContent = `<strong>${params[0].name}</strong><br/>`;

                    params.forEach((item: any) => {
                        if (item.seriesName === 'Anomaly monitoring items' && alertData.firingAlertNames) {
                            const alertNames = alertData.firingAlertNames[item.name] || [];
                            tooltipContent += `<span style="color:#ff6b6b;">🔴 Firing monitoring items: ${item.value} items</span><br/>`;
                            tooltipContent += alertNames.length ? alertNames.map((alert: any) => `- ${alert}`).join('<br/>') : 'None';
                            tooltipContent += `<br/><br/>`;
                        }
                    });

                    return tooltipContent;
                }
            },
            legend: {
                data: ['Normally monitoring items', 'Anomaly monitoring items'],
                top: 40,
                left: 'center',
                textStyle: {color: '#FFFFFF'}
            },
            grid: {
                left: '5%',
                right: '5%',
                bottom: '5%',
                top: 80,
                containLabel: true
            },
            xAxis: {
                type: 'value',
                boundaryGap: [0, 0.01],
                axisLabel: {show: false},
                splitLine: {show: false},
                axisTick: {show: false},
                axisLine: {show: false}
            },
            yAxis: {
                type: 'category',
                data: alertData.organizations,
                axisLine: {lineStyle: {color: '#FFFFFF'}},
                axisTick: {show: false},
                axisLabel: {margin: 20}
            },
            series: [
                {
                    name: 'Anomaly monitoring items',
                    type: 'bar',
                    stack: 'total',
                    barWidth: 50,
                    barMaxWidth: 80,
                    label: {
                        show: true,
                        position: 'inside',
                        formatter: (params: { value: any }) => params.value ? `${params.value} items` : ''
                    },
                    data: firingItemsAdjusted,
                    itemStyle: {color: '#ff6b6b'}
                },
                {
                    name: 'Normally monitoring items',
                    type: 'bar',
                    stack: 'total',
                    barWidth: 50,
                    barMaxWidth: 80,
                    label: {
                        show: true,
                        position: 'inside',
                        formatter: (params: { value: any }) => params.value ? `${params.value} items` : ''
                    },
                    data: totalItemsAdjusted,
                    itemStyle: {color: '#4caf50'}
                }
            ]
        };
    };


    const renderContent = () => {
        if (loading) return <p>Loading...</p>;
        if (userType === 'platform_admin') return <ReactECharts option={getHardwareUsageOption()}
                                                                style={{height: '400px'}}/>;
        return <ReactECharts option={getAlertSummaryOption()} style={{height: '400px'}}/>;
    };

    return (
        <Container fluid className="dashboardpage-main-content">
            <Row className="mb-4">
                <Col><WelcomeHeader title="Welcome to LynxGuard"/></Col>
            </Row>
            <Row><Col>{renderContent()}</Col></Row>
        </Container>
    );
};

export default DashboardPage;
